import {useCallback, useEffect, useRef, useState} from 'react';
import styles from './adminuser.module.scss';
import adminstyles from './adminuser_list.module.scss';
import {Search} from '@dabafinance/react-components';
import UserCard from './card/UserCard';
import {GET_ALL_USERS} from 'graphql/queries/users';
import {useQuery} from '@apollo/client';
import UserTable from './card/UserTable';
import UnverifiedUserTable from './card/unverifiedUser';
import {Empty, Pagination, Select, Spin, Tooltip} from 'antd';
import {sanitizeInput} from 'utils/helper';
import filterIcon from 'icons/filter.svg';
import UsersFilter from './userDetails/UsersFIlter/UsersFilter';
import moment from 'moment';
import {motion, AnimatePresence} from 'framer-motion';
import {REGION_CATEGORY_FILTER_OPTIONS} from 'utils/constants';
import {
  activeFilterChips,
  activeFilterCount,
  clearedFilterValues,
} from './activeFilters';

const buildUserFilterInput = (merged, userTypeMeta, searchUsername) => {
  let {
    kycStatus,
    depositStatus,
    nationality,
    city,
    country,
    profileCompletion,
    hasMadeReferral,
    startDate,
    endDate,
    walletTransactionType,
    hasMadeDeposit,
    hasMadeInvestment,
    walletTransactionStartDate,
    walletTransactionEndDate,
    regionCategory,
    riskProfile,
    userSegments,
    portfolioWeight,
  } = merged || {};

  if (startDate) {
    startDate = moment(startDate).format();
  }
  if (endDate) {
    endDate = moment(endDate).format();
  }
  if (walletTransactionStartDate) {
    walletTransactionStartDate = moment(walletTransactionStartDate).format();
  }
  if (walletTransactionEndDate) {
    walletTransactionEndDate = moment(walletTransactionEndDate).format();
  }

  return {
    [userTypeMeta?.filterProperty]:
      userTypeMeta?.filterPropertyValue ?? kycStatus,
    depositStatus,
    nationality,
    city,
    startDate,
    endDate,
    profileCompletion,
    walletTransactionType,
    hasMadeReferral,
    walletTransactionEndDate,
    walletTransactionStartDate,
    hasMadeDeposit,
    hasMadeInvestment,
    country,
    username: searchUsername,
    ...(Array.isArray(regionCategory) && regionCategory.length > 0
      ? {regionCategory}
      : {}),
    ...(Array.isArray(riskProfile) && riskProfile.length > 0
      ? {riskProfile}
      : {}),
    ...(Array.isArray(userSegments) && userSegments.length > 0
      ? {userSegments}
      : {}),
    ...(Array.isArray(portfolioWeight) && portfolioWeight.length > 0
      ? {portfolioWeight}
      : {}),
  };
};

const AdminUserList = ({
  userType,
  switchView,
  onSetStatistics,
  onLoadingUser,
  userTypeMeta,
  currentPage,
  operations,
  setCurrentPage,
  onPageReset,
  filterValues,
  setFilterValues,
}) => {
  const [searchData, setSearchData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [limit, setLimit] = useState(40);
  const [openFilterModal, setOpenFilterModal] = useState(false);

  const {data, loading, refetch} = useQuery(GET_ALL_USERS, {
    variables: {
      filters: {
        pagination: {
          page: 1,
          limit: 40,
        },
        sort: {
          order: 'DESC',
          field: 'createdAt',
        },
        user: {
          [userTypeMeta?.filterProperty]: userTypeMeta?.filterPropertyValue,
        },
      },
    },
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      onSetStatistics?.(data?.statistics?.statistics);
      setSearchData(data?.getAllUsers?.users);
    },
    errorPolicy: 'ignore',
  });

  /**
   * Page size changes refetch — but only after the first render.
   *
   * This effect used to run on mount too, firing a second identical
   * `GetAllUsers` immediately behind the query's own initial fetch. That query
   * also carries the statistics aggregate, so every visit to this page paid
   * for it twice.
   */
  const limitMounted = useRef(false);
  useEffect(() => {
    if (!limitMounted.current) {
      limitMounted.current = true;
      return;
    }
    handleFilter(filterValues);
  }, [limit]);

  useEffect(() => {
    setSearchTerm('');
    setFilterValues({});
  }, [userType]);

  /** Merges partial values in — what the filter panel submits. */
  const handleFilter = (modalValues, page) => {
    const merged =
      modalValues != null
        ? {...filterValues, ...modalValues}
        : {...filterValues};
    applyFilters(merged, page);
    setOpenFilterModal(false);
  };

  /**
   * Replaces the filter set wholesale.
   *
   * Removing a chip needs this: `handleFilter` merges, so it can add and
   * change a filter but never take one away.
   */
  const applyFilters = (nextValues, page) => {
    setFilterValues(nextValues);
    refetch({
      filters: {
        pagination: {
          page: page ?? currentPage,
          limit,
        },
        sort: {
          order: 'DESC',
          field: 'createdAt',
        },
        user: buildUserFilterInput(nextValues, userTypeMeta, searchTerm),
      },
    });
  };

  useEffect(() => {
    onLoadingUser(loading);
  }, [loading]);

  const handlePageChange = page => {
    if (page === currentPage) return;
    setCurrentPage(page);
    handleFilter(filterValues, page);
  };

  /**
   * This is a debounce function that delays the execution of a function until a certain amount of time
   * has passed without the function being called again.
   * @returns A new function that wraps the original function passed as an argument and adds a debounce
   * functionality to it.
   */
  const debounce = func => {
    let timer;
    return function (...args) {
      const context = this;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        func.apply(context, args);
      }, 1200);
    };
  };

  const handleUserSearch = (
    searchTermArg,
    pageLimit,
    existingFilters,
    typeMeta,
  ) => {
    const sanitizedValue = sanitizeInput(searchTermArg);
    setFilterValues(values => ({...values, username: sanitizedValue}));
    setCurrentPage(1);
    onPageReset();
    refetch({
      filters: {
        pagination: {
          page: 1,
          limit: pageLimit,
        },
        sort: {
          order: 'DESC',
          field: 'createdAt',
        },
        user: buildUserFilterInput(
          existingFilters,
          typeMeta,
          sanitizedValue || '',
        ),
      },
    });
  };

  const handleRegionCategoryChange = next => {
    const merged = {...filterValues};
    if (next?.length) merged.regionCategory = next;
    else delete merged.regionCategory;
    setCurrentPage(1);
    onPageReset();
    applyFilters(merged, 1);
  };

  /** Drops one filter and re-runs the query without it. */
  const removeFilter = key => {
    const next = {...filterValues};
    delete next[key];
    setCurrentPage(1);
    onPageReset();
    applyFilters(next, 1);
  };

  const clearAllFilters = () => {
    setCurrentPage(1);
    onPageReset();
    applyFilters(clearedFilterValues(filterValues), 1);
  };

  const optimizedVersion = useCallback(debounce(handleUserSearch), []);

  const chips = activeFilterChips(filterValues);
  const filterCount = activeFilterCount(filterValues);
  const total = data?.getAllUsers?.totalDocs ?? 0;

  /**
   * `totalDocs` counts every record the query matched — and the list carries
   * soft-deleted accounts. On the unfiltered All Users tab that is 5,325
   * records against 2,877 live users, the exact difference being the 2,448
   * accounts `user: {deleted: true}` returns. So the header was reporting
   * roughly twice the number of users the platform actually has, and
   * disagreeing with the Total Users tile directly above it.
   *
   * The live figure is used wherever it is the truthful one. It cannot be used
   * under a filter: `statistics` is a separate unfiltered aggregate that
   * ignores the `user` input entirely, so on a filtered view only `totalDocs`
   * reflects what is on screen.
   *
   * The deleted records cannot simply be dropped from the list — the gateway
   * honours `deleted: true` but ignores `deleted: false`, so they are counted
   * out and called out instead of hidden.
   */
  const liveUsers = data?.statistics?.statistics?.totalUsers;
  const isUnfilteredAll =
    userType === 'ALL' && !searchTerm && filterCount === 0;
  const showLiveCount = isUnfilteredAll && typeof liveUsers === 'number';
  const deletedCount = showLiveCount ? Math.max(0, total - liveUsers) : 0;
  const hasRows = Boolean(searchData?.length);
  // ACCREDITED is filtered by `isAccredited`, not by any of the panel's
  // fields, so the panel and the region select are hidden on that tab.
  const filtersAvailable = userType !== 'ACCREDITED';

  const RenderListTable = () => {
    if (['ALL', 'VERIFIED', 'PENDING'].includes(userType)) {
      return (
        <UserTable
          loading={loading}
          className={styles['user-table']}
          data={searchData}
          operations={operations}
          pagination={false}
        />
      );
    }

    return (
      <UnverifiedUserTable
        loading={loading}
        data={searchData}
        operations={operations}
        className={styles['user-table']}
        pagination={false}
      />
    );
  };

  const emptyState = (
    <div className={styles.emptyState}>
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <div className={styles.emptyCopy}>
            <h3>No users match this view</h3>
            <p>
              {searchTerm || filterCount
                ? 'Nothing here fits the current search and filters. Try widening them.'
                : `There are no ${userTypeMeta?.labelTitle?.toLowerCase()} yet.`}
            </p>
          </div>
        }
      />
      {(searchTerm || filterCount > 0) && (
        <button
          type="button"
          className={styles.clearAllButton}
          onClick={clearAllFilters}>
          Clear filters
        </button>
      )}
    </div>
  );

  return (
    <div className={adminstyles.wrapper}>
      <div className={styles['investors-header-grid']}>
        <div className={styles.investorsHeaderTitleRow}>
          <div className={styles['title']}>{userTypeMeta.labelTitle}</div>
          {/* The row count belongs beside the title: it is the one number
              that says whether a filter did what you expected. */}
          <span className={styles.resultCount}>
            {loading
              ? 'Loading…'
              : `${(showLiveCount ? liveUsers : total).toLocaleString()} total`}
          </span>
          {!loading && deletedCount > 0 && (
            <Tooltip
              title={`The list below also shows ${deletedCount.toLocaleString()} deleted accounts, which is why it pages past ${liveUsers.toLocaleString()}. They cannot be filtered out from here.`}>
              <span className={styles.deletedCount}>
                +{deletedCount.toLocaleString()} deleted
              </span>
            </Tooltip>
          )}
        </div>
        <div className={styles.filters}>
          <div className={styles.searchBarWrap}>
            <Search
              className={styles['search-bar']}
              placeholder="Name, Username, Email(s), Account No."
              value={searchTerm}
              onChange={e => {
                setSearchTerm(e);
                optimizedVersion(e, limit, filterValues, userTypeMeta);
              }}
              id={'user-search'}
            />
          </div>
          {filtersAvailable && (
            <div className={styles.listToolbarRegion}>
              <span className={styles.regionFilterLabel}>Region(s)</span>
              <Select
                mode="multiple"
                allowClear
                placeholder="All regions"
                className={styles.regionFilterSelect}
                maxTagCount={2}
                optionFilterProp="label"
                value={filterValues.regionCategory || []}
                onChange={handleRegionCategoryChange}
                options={REGION_CATEGORY_FILTER_OPTIONS}
              />
            </div>
          )}
          {filtersAvailable && (
            <>
              <button
                type="button"
                onClick={() => setOpenFilterModal(!openFilterModal)}
                className={`${styles.filter} ${
                  filterCount ? styles.filterOn : ''
                }`}>
                <img src={filterIcon} alt="" />
                <span>Filter</span>
                {/* The button used to look identical whether nothing or nine
                    filters were applied. */}
                {filterCount > 0 && (
                  <span className={styles.filterCount}>{filterCount}</span>
                )}
              </button>
              <AnimatePresence>
                {openFilterModal && (
                  <motion.div
                    className={styles.filterModal}
                    initial={{opacity: 0, y: -12}}
                    animate={{opacity: 1, y: 0}}
                    exit={{opacity: 0, y: -12}}
                    transition={{duration: 0.22, ease: 'easeOut'}}>
                    <UsersFilter
                      handleClose={() => setOpenFilterModal(false)}
                      handleFilter={handleFilter}
                      onReset={clearAllFilters}
                      activePage={userType}
                      savedFilterValues={filterValues}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}
        </div>
      </div>

      {/* What is actually applied, and how to undo any of it. */}
      {chips.length > 0 && (
        <div className={styles.chipBar}>
          {chips.map(chip => (
            <button
              key={chip.key}
              type="button"
              className={styles.chip}
              onClick={() => removeFilter(chip.key)}
              title={`Remove ${chip.name} filter`}>
              <span className={styles.chipName}>{chip.name}</span>
              <span className={styles.chipValue}>{chip.display}</span>
              <span className={styles.chipRemove}>×</span>
            </button>
          ))}
          <button
            type="button"
            className={styles.clearAll}
            onClick={clearAllFilters}>
            Clear all
          </button>
        </div>
      )}

      {switchView ? (
        loading ? (
          <div className={styles['users-card']}>
            {Array(8)
              .fill('')
              .map((data, key) => (
                <div key={key} className={styles.loadingSkeleton}>
                  {data}
                </div>
              ))}
          </div>
        ) : hasRows ? (
          <div className={styles['users-card']}>
            {searchData.map(user => (
              <UserCard
                key={user?.id}
                id={user?.id}
                firstName={user?.firstName}
                lastName={user?.lastName}
                data={user}
                operations={operations}
                tab="All Users"
                imageUrl={user?.imageUrl}
              />
            ))}
          </div>
        ) : (
          emptyState
        )
      ) : loading && !hasRows ? (
        <div className={styles.tableLoading}>
          <Spin size="large" />
        </div>
      ) : hasRows ? (
        <RenderListTable />
      ) : (
        emptyState
      )}

      {/* A pager under an empty table is noise. */}
      {total > 0 && (
        <Pagination
          current={currentPage}
          onChange={e => handlePageChange(e)}
          showQuickJumper
          pageSize={limit}
          total={total}
          className="ant-table-pagination"
          showSizeChanger
          onShowSizeChange={(_, size) => {
            setLimit(size);
          }}
        />
      )}
    </div>
  );
};

export {AdminUserList};
